import type { AIVoice, AIVoiceCloneLimit, PatchedEditAIVoiceCloneRequest } from 'js/src/client';
import { ApiService, TaskResultMessageTypeEnum, UploadUrlsRequestTypeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { buildPublicGcsUrl } from 'js/src/libs/gcs-url';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';

function getFileExtensionFromBlob(blob: Blob): string {
    const mimeToExt: Record<string, string> = {
        'audio/webm': 'webm',
        'audio/ogg': 'ogg',
        'audio/mp4': 'mp4',
        'audio/mpeg': 'mp3',
        'audio/wav': 'wav',
        'audio/x-m4a': 'm4a',
    };
    return mimeToExt[blob.type] ?? 'webm';
}

/**
 * Uploads an audio blob to GCS using the same flow as UploadMediaController:
 * 1. Get a signed upload URL from uploadUrlsCreate
 * 2. PUT the blob to GCS
 * 3. Construct the public download URL from the response bucket + file_name
 *
 * Passing an AbortSignal cancels the in-flight upload (the API call for
 * the signed URL completes quickly and is not cancellable).
 */
async function uploadAudioBlobToGcs(blob: Blob, signal?: AbortSignal): Promise<string> {
    const ext = getFileExtensionFromBlob(blob);
    const fileName = `voice-clone-${uuid()}.${ext}`;

    // Step 1: Get signed upload URL
    const { data } = await ApiService.uploadUrlsCreate({
        body: {
            file_name: fileName,
            type: UploadUrlsRequestTypeEnum.SINGLE_REQUEST_UPLOAD,
        },
    });

    // Step 2: Upload blob to GCS
    const response = await fetch(data.upload_url, {
        method: 'PUT',
        body: blob,
        signal,
    });

    if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
    }

    // Step 3: Construct the public download URL from the API response's bucket +
    // file_name (not the upload URL, whose host the same-origin path may rewrite).
    return buildPublicGcsUrl({ bucket: data.bucket, file_name: data.file_name });
}

export async function getVoiceCloneLimit(signal?: AbortSignal): Promise<AIVoiceCloneLimit> {
    const { data } = await ApiService.aiVoiceCloneLimitRetrieve({ signal });
    return data;
}

/**
 * Uploads the audio blob to GCS and starts the clone_ai_voice backend task.
 * Returns the Celery task_id, which is the same id the task uses when it
 * publishes its result over the websocket (see handle_success in
 * task_result/utils.py).
 *
 * The signal aborts the GCS upload if provided. Once the backend task is
 * created it cannot be cancelled from the frontend — callers that need full
 * cancellation should also abort the subsequent waitForVoiceCloneResult.
 */
export async function startVoiceClone(blob: Blob, signal?: AbortSignal): Promise<string> {
    const gcsUrl = await uploadAudioBlobToGcs(blob, signal);
    return startVoiceCloneFromUrl(gcsUrl, signal);
}

export async function startVoiceCloneFromUrl(
    gcsUrl: string,
    signal?: AbortSignal,
): Promise<string> {
    signal?.throwIfAborted();
    const { data } = await ApiService.aiVoiceCloneCreate({
        body: {
            name: 'Untitled voice',
            gcs_url: gcsUrl,
            remove_background_noise: true,
        },
    });

    return data.task_id;
}

/**
 * Waits for the voice cloning task to complete via websocket and returns the
 * created AIVoice (with preview_url).
 *
 * Passing an AbortController cancels the wait; note that the backend task
 * keeps running server-side. Orphaned clones surface on next refetch of the
 * voice list and can be removed via deleteVoiceClone once their slug is known.
 */
export async function waitForVoiceCloneResult(
    taskId: string,
    abortController?: AbortController,
): Promise<AIVoice> {
    const ws = WebSocketClient.getInstance();
    // Backend `clone_ai_voice` uses the Celery task id (self.request.id) — not
    // an AsyncOperation row — so this is genuinely a TASK_RESULT path. The task
    // name is passed as a string literal rather than AsyncOperationNameEnum
    // to satisfy the type/uid pairing constraint in waitForMessage.
    return ws.waitForMessage<AIVoice>(
        taskId,
        TaskResultMessageTypeEnum.TASK_RESULT,
        'clone_ai_voice',
        { abortController },
    );
}

export type EditVoiceCloneConfig = {
    name: string;
    language: string | null;
    style: string | null;
    tone: string | null;
    gender: string | null;
    age: string | null;
};

export async function editVoiceClone(
    slug: string,
    config: EditVoiceCloneConfig,
): Promise<AIVoice | null> {
    try {
        const body: PatchedEditAIVoiceCloneRequest = {
            name: config.name,
            filters: {
                language: config.language,
                style: config.style,
                tone: config.tone,
                gender: config.gender,
                age: config.age,
            },
        };

        const { data } = await ApiService.aiVoiceClonePartialUpdate2({
            body,
            path: { slug },
        });
        Toast.success('Voice updated successfully.');
        return data;
    } catch (e) {
        Toast.error('Something went wrong updating your voice. Please try again.');
        return null;
    }
}

/**
 * Deletes a cloned voice. Pass `silent: true` when the delete is part of a
 * cleanup path (e.g. the user cancelled before saving a freshly-cloned voice)
 * and the user should not see a "Voice deleted" toast.
 */
export async function deleteVoiceClone(
    slug: string,
    { silent }: { silent?: boolean } = {},
): Promise<boolean> {
    try {
        await ApiService.aiVoiceCloneDestroy2({
            path: { slug },
        });
        if (!silent) Toast.success('Voice deleted.');
        return true;
    } catch (e) {
        if (!silent) Toast.error('Something went wrong deleting your voice. Please try again.');
        return false;
    }
}
